import { Box, Button, useTheme } from "@mui/material";
import { useId } from "react";
import { useReminderTypesQuery } from "web_myplann/Api/Reminders/useReminderTypesQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderCreateFields } from "web_myplann/Reminders/Components/ReminderCreateFields";
import { useReminderCreateForm } from "web_myplann/Reminders/Hooks/useReminderCreateForm";
import type {
	Reminder,
	ReminderEntity,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { useSheetBottomSpacing } from "web_myplann/Shared/Hooks/useSheetBottomSpacing";
import { findSameDayReminder } from "web_myplann/Shared/Utils/findSameDayReminder";

type Props = {
	entity: ReminderEntity;
	existingReminders: Reminder[];
	open: boolean;
	onClose: () => void;
	onOpenExisting: (reminder: Reminder) => void;
};

export const ReminderCreateDrawer = ({
	entity,
	existingReminders,
	open,
	onClose,
	onOpenExisting,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();
	const bottomSpacing = useSheetBottomSpacing(theme.spacingTokens.lg);
	const duplicateAlertId = useId();

	const { data: templates = [] } = useReminderTypesQuery(entity);

	const { form, submit, isSubmitting } = useReminderCreateForm({
		entity,
		templates,
		onSuccess: onClose,
	});

	const date = form.watch("date");
	const duplicate =
		date instanceof Date ? findSameDayReminder(existingReminders, date) : null;

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(Resources.Reminders.Form.addTitle)}
			grabber
			paperSx={{ maxHeight: "100%" }}
		>
			<ReminderCreateFields
				form={form}
				duplicate={duplicate}
				onOpenDuplicate={onOpenExisting}
				duplicateAlertId={duplicateAlertId}
			/>

			<Box
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.lg}
				pb={bottomSpacing}
			>
				<Button
					variant="primary"
					onClick={submit}
					aria-describedby={duplicate ? duplicateAlertId : undefined}
					disabled={
						!form.formState.isValid || isSubmitting || duplicate !== null
					}
				>
					{t(Resources.Reminders.Form.addCta)}
				</Button>
			</Box>
		</MyPlannDrawer>
	);
};
